'use client';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import {
  LeagueGameDayHeaderDetails,
  MatchGroup,
  PlayersOnBench,
} from '@/modules/league/new-components/types';
import { getNumberSuffix } from '@/utils/helpers/getNumberSuffix';

interface GroupsCardProps {
  players: PlayersOnBench[] | undefined;
  groupSize: number | undefined;
  gameDayInfo: LeagueGameDayHeaderDetails;
  changeOrderInGroup: (fromIndex: number, toIndex: number) => void;
  matchDateId: string;
  matchesStarted: boolean;
  matchesCompleted: boolean;
  playerType: string | undefined;
  matchGroups: MatchGroup[];
  isAuthorized: boolean | null;
  userUuid: string | undefined;
}

export const BenchCard = (props: GroupsCardProps) => {
  const {
    players,
    groupSize,
    gameDayInfo,
    changeOrderInGroup,
    playerType,
    matchesStarted,
    isAuthorized,
    matchesCompleted,
    userUuid,
  } = props;

  const [selectedPlayer, setSelectedPlayer] = useState({
    firstName: '',
    lastName: '',
    userId: '',
  });

  const [showOptOut, setShowOptOut] = useState(false);
  const [showDoupleConfirmOptOutSinglePlayer, setShowDoupleConfirmOptOutSinglePlayer] =
    useState(false);

  return (
    <>
      <div className='my-2 h-auto w-full gap-12 rounded-lg border bg-gray-100 px-2 py-3 shadow-sm md:p-4'>
        <div className='items-left mb-2 flex flex-col justify-between gap-1 md:h-[30px] md:flex-row md:items-center'>
          <div className='flex justify-between md:hidden'>
            <h3 className='flex text-base font-semibold'>Bench</h3>
            <p className='text-right text-sm text-gray-500'>
              {groupSize && groupSize > 1 ? `${groupSize} players` : `${groupSize} player`}
            </p>
          </div>

          <h3 className='hidden text-lg font-bold text-gray-900 md:flex'>Bench</h3>
          <div className='hidden justify-between gap-4 md:flex'>
            <p className='text-sm text-gray-500'>
              {groupSize && groupSize > 1 ? `${groupSize} players` : `${groupSize} player`}
            </p>
          </div>
        </div>
        <div className='hidden border-t-[0.5px] border-gray-200 p-2 md:block' />
        <div className='flex w-full flex-col gap-3'>
          {players
            ? players?.map((player, i) => {
                const isUserAlsoPlayer = userUuid === player.SessionTeamID;
                return (
                  <div
                    key={`player-${i}`}
                    className={`w-full ${gameDayInfo.isMatchStarted ? (isUserAlsoPlayer ? 'rounded-lg border-2 border-leagues-300/50 p-3 hover:border-leagues-300/90 md:p-4' : 'rounded-lg border-2 p-3 hover:border-leagues-300/10 md:p-4') : ''}`}
                  >
                    <div className={`flex items-center gap-2`}>
                      <div className='flex w-full flex-row gap-2 md:gap-4'>
                        {matchesStarted || !isAuthorized ? null : (
                          <div
                            className={`flex w-full max-w-16 items-center ${i === players.length - 1 ? 'justify-end' : 'justify-between'} text-gray-900 md:max-w-16`}
                          >
                            <FontAwesomeIcon
                              onClick={() =>
                                changeOrderInGroup(player.SeedNumber - 1, player.SeedNumber - 2)
                              }
                              icon={faArrowUp}
                              className='flex size-3 items-center justify-center rounded-full border-2 border-leagues-700 bg-gray-100 p-1 text-sm text-leagues-700 hover:cursor-pointer hover:bg-leagues-500 hover:text-white md:p-1 md:text-base'
                            />
                            {i !== players.length - 1 && (
                              <FontAwesomeIcon
                                onClick={() =>
                                  changeOrderInGroup(player.SeedNumber - 1, player.SeedNumber)
                                }
                                icon={faArrowDown}
                                className='flex size-3 items-center justify-center rounded-full border-2 border-leagues-700 bg-gray-100 p-1 text-sm text-leagues-700 hover:cursor-pointer hover:bg-leagues-500 hover:text-white md:text-base'
                              />
                            )}
                          </div>
                        )}

                        <div
                          className={`flex w-full flex-col gap-3 rounded-md hover:bg-gray-200/30 md:gap-3 ${!gameDayInfo.isMatchStarted ? (isUserAlsoPlayer ? 'border-leagues-300/50' : 'border p-3 shadow-sm') : ''}`}
                        >
                          <div className='flex w-full flex-row justify-between font-semibold'>
                            <div
                              className={`flex items-center text-xs font-medium text-leagues-500 ${!gameDayInfo.isMatchStarted ? 'justify-start italic' : 'size-[25px] justify-center rounded-full bg-leagues-300/20 p-2'}`}
                            >
                              {gameDayInfo.isMatchStarted
                                ? player.SeedNumber
                                : `${player.SeedNumber}${getNumberSuffix(player.SeedNumber)} place - ${player.SeedNumber}${getNumberSuffix(player.SeedNumber)} seed`}
                            </div>
                            {(playerType === 'Partner' && players.length > 2) ||
                            players.length > 2 ||
                            players.length > 4 ? (
                              <>
                                {!matchesCompleted && isAuthorized ? (
                                  <p
                                    className={`cursor-pointer justify-end text-sm font-semibold text-leagues-700 hover:text-leagues-500`}
                                    onClick={() => {
                                      setShowOptOut(true);
                                      setSelectedPlayer({
                                        firstName: player.FirstName,
                                        lastName: player.LastName,
                                        userId: player.SessionTeamID,
                                      });
                                    }}
                                  >
                                    Opt out
                                  </p>
                                ) : null}
                              </>
                            ) : null}
                          </div>

                          {!isAuthorized && (
                            <p
                              className={`font-semibold ${!gameDayInfo.isMatchStarted ? 'pb-1' : 'mb-2'}`}
                            >
                              {player.FirstName} {player.LastName}
                            </p>
                          )}

                          {isAuthorized && (
                            <div
                              className={`flex w-full flex-row justify-between gap-2 font-semibold`}
                            >
                              <div className='flex flex-col'>
                                <p className={`line-clamp-3`}>
                                  {player.FirstName} {player.LastName}
                                </p>
                              </div>
                            </div>
                          )}
                          {gameDayInfo.isMatchStarted && (
                            <>
                              <p className='text-sm text-gray-500 md:flex'>
                                League: {player.SeedNumber}
                                {getNumberSuffix(player.SeedNumber)} seed
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      </div>
      <ConfirmationModal
        open={showOptOut}
        onClose={() => setShowOptOut(false)}
        onConfirm={() => {
          setShowDoupleConfirmOptOutSinglePlayer(true);
          setShowOptOut(false);
        }}
        title='Are you sure?'
        message={`This will opt out ${selectedPlayer.firstName} ${selectedPlayer.lastName}`}
        confirmButtonText='Yes'
      />
      <ConfirmationModal
        open={showDoupleConfirmOptOutSinglePlayer}
        onClose={() => setShowDoupleConfirmOptOutSinglePlayer(false)}
        onConfirm={() => {
          setShowDoupleConfirmOptOutSinglePlayer(false);
        }}
        title='Have to ask again. Are you sure?'
        message={`This will opt out ${selectedPlayer.firstName} ${selectedPlayer.lastName} from this game day.`}
        confirmButtonText='Yes'
      />
    </>
  );
};
